import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, MapPin } from "lucide-react";
import { Logo } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/plans", label: "Plans" },
  { to: "/shares", label: "Shares" },
  { to: "/projects", label: "Projects" },
  { to: "/referral-program", label: "Referral" },
  { to: "/faq", label: "FAQ" },
];

export function PublicNavbar() {
  const [open, setOpen] = useState(false);
  const { user, isAdmin } = useAuth();
  const nav = useNavigate();
  const dash = isAdmin ? "/admin" : "/dashboard";
  return (
    <header className="sticky top-0 z-50 glass border-b border-cyan-500/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm text-slate-300 hover:text-cyan-300 transition-colors">{l.label}</Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <button data-testid="nav-dashboard-btn" onClick={() => nav(dash)}
              className="btn-glow px-5 py-2 rounded-full text-sm font-semibold text-slate-950">Dashboard</button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link"
                className="text-sm text-slate-200 hover:text-cyan-300 px-4 py-2">Login</Link>
              <Link to="/signup" data-testid="nav-signup-link"
                className="btn-glow px-5 py-2 rounded-full text-sm font-semibold text-slate-950">Create Account</Link>
            </>
          )}
        </div>
        <button data-testid="mobile-menu-btn" className="md:hidden text-slate-200" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden glass border-t border-cyan-500/10 px-4 py-4 space-y-2">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block py-2 text-slate-300 hover:text-cyan-300">{l.label}</Link>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {user ? (
              <button onClick={() => nav(dash)} className="btn-glow py-2.5 rounded-full text-sm font-semibold text-slate-950">Dashboard</button>
            ) : (
              <>
                <Link to="/login" className="py-2.5 text-center rounded-full border border-cyan-500/30 text-slate-200">Login</Link>
                <Link to="/signup" className="btn-glow py-2.5 text-center rounded-full text-sm font-semibold text-slate-950">Create Account</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const cols = [
    { h: "Platform", links: [["Home", "/"], ["About", "/about"], ["How It Works", "/how-it-works"], ["Plans", "/plans"], ["Company Shares", "/shares"], ["Upcoming Projects", "/projects"]] },
    { h: "Program", links: [["Referral", "/referral-program"], ["FAQ", "/faq"], ["Contact", "/contact"]] },
    { h: "Legal", links: [["Terms", "/terms"], ["Privacy", "/privacy"], ["Risk Disclosure", "/risk-disclosure"]] },
  ];
  return (
    <footer className="border-t border-cyan-500/10 bg-[#050811] mt-24">
      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <Logo />
          <p className="text-sm text-slate-500 mt-4 leading-relaxed max-w-xs">
            Next-generation digital asset technology platform. Digital assets carry significant risk.
          </p>
          <p className="text-xs text-slate-500 mt-4 leading-relaxed max-w-xs flex gap-2" data-testid="footer-address">
            <MapPin className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
            <span>Pathumthani Rd., Kubangluang, Khu Bang Luang, Lat Lum Kaeo District, Pathum Thani 12140, Thailand</span>
          </p>
        </div>
        {cols.map((c) => (
          <div key={c.h}>
            <h4 className="font-heading text-sm text-white mb-4">{c.h}</h4>
            <ul className="space-y-2.5">
              {c.links.map(([label, to]) => (
                <li key={to}><Link to={to} className="text-sm text-slate-400 hover:text-cyan-300 transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-cyan-500/10 py-6 px-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs text-slate-600">
        <span>© {new Date().getFullYear()} AI TECHNOLOGY. All rights reserved. Returns are variable and never guaranteed.</span>
        <Link to="/admin/login" data-testid="footer-admin-link" className="text-slate-600 hover:text-cyan-300 transition-colors inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Admin</Link>
      </div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="ait-bg min-h-screen flex flex-col">
      <PublicNavbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
